import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HERO_IMAGE } from "@/lib/constants";
import { Gift } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = (params.get("ref") || "").trim().toUpperCase();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company: "", referral_code: refFromUrl });
  const [busy, setBusy] = useState(false);
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    if (!refFromUrl) return;
    api.get(`/referrals/validate/${refFromUrl}`).then((r) => {
      if (r.data.valid) setReferrer(r.data);
    }).catch(() => {});
  }, [refFromUrl]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success(referrer ? "Account created. Use code FRIEND50 at checkout for 10% off." : "Account created.");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#14100c] text-[#f4ede2]">
      <div className="relative hidden md:block">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 side-scrim" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded bg-gradient-to-br from-[#d4a574] to-[#a67b4c] flex items-center justify-center text-[#14100c] font-black font-display">C</div>
            <div>
              <div className="font-display font-bold text-[16px]">The Close Out</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#d4a574]">Distress Intelligence</div>
            </div>
          </Link>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Request Access</div>
            <h1 className="font-display text-3xl font-bold">Create your account.</h1>
          </div>

          {referrer && (
            <div className="flex gap-3 items-start p-3 rounded border border-[#34d399]/30 bg-[#34d399]/5" data-testid="referral-banner">
              <Gift size={16} className="text-[#34d399] mt-0.5 shrink-0" />
              <div className="text-[12px]">
                <div className="font-semibold text-[#34d399]">You've been invited by {referrer.referrer_name}{referrer.referrer_company ? ` (${referrer.referrer_company})` : ""}.</div>
                <div className="text-[#b8ac96] mt-0.5">Use code <span className="font-mono text-[#d4a574]">FRIEND50</span> at checkout for 10% off your first month.</div>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Full name</Label>
            <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="reg-name" className="bg-[#1c1712] border-[#33291f]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Work email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" className="bg-[#1c1712] border-[#33291f]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Company</Label>
            <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="reg-company" className="bg-[#1c1712] border-[#33291f]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#b8ac96]">Password</Label>
            <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reg-password" className="bg-[#1c1712] border-[#33291f]" />
          </div>
          <button type="submit" disabled={busy} data-testid="reg-submit" className="w-full py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold hover:bg-[#e8b988] disabled:opacity-60">
            {busy ? "Creating…" : "Create Account →"}
          </button>
          <div className="text-center text-xs text-[#7a6d5a]">
            Already have an account? <Link to="/login" className="text-[#d4a574]">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
